import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson, putJson } from '../lib/api';

// --- Types ---

export type SequenceStatus = 'draft' | 'active' | 'paused' | 'archived';
export type SequenceStepType = 'email' | 'wait' | 'task' | 'condition';
export type EnrollmentStatus = 'active' | 'completed' | 'unenrolled' | 'failed';

export interface SequenceStep {
  id: string;
  order: number;
  type: SequenceStepType;
  name: string;
  templateId?: string;
  delayDays?: number;
  delayHours?: number;
  taskDescription?: string;
  conditionField?: string;
  conditionOperator?: string;
  conditionValue?: string;
}

export interface Sequence {
  id: string;
  name: string;
  description?: string;
  status: SequenceStatus;
  steps: SequenceStep[];
  enrollmentCount: number;
  completionRate: number;
  createdAt: string;
  updatedAt: string;
}

export interface Enrollment {
  id: string;
  sequenceId: string;
  contactId: string;
  contactName?: string;
  contactEmail?: string;
  status: EnrollmentStatus;
  currentStepIndex: number;
  enrolledAt: string;
  completedAt?: string;
  unenrolledAt?: string;
}

export interface SequenceFilters {
  status?: SequenceStatus;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface SequencesResponse {
  sequences: Sequence[];
  total: number;
  limit: number;
  offset: number;
}

export interface EnrollmentsResponse {
  enrollments: Enrollment[];
  total: number;
  limit: number;
  offset: number;
}

// --- Helpers ---

function buildQueryString(filters?: SequenceFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.search) params.set('search', filters.search);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.offset) params.set('offset', String(filters.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// --- Hooks ---

export function useSequences(filters?: SequenceFilters) {
  return useQuery({
    queryKey: ['sequences', filters],
    queryFn: () => fetchJson<SequencesResponse>(`/api/sequences${buildQueryString(filters)}`),
  });
}

export function useSequence(id: string | null) {
  return useQuery({
    queryKey: ['sequence', id],
    queryFn: () => fetchJson<Sequence>(`/api/sequences/${id}`),
    enabled: !!id,
  });
}

export function useCreateSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Sequence>) => postJson<Sequence>('/api/sequences', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
    },
  });
}

export function useEnrollContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sequenceId, contactId }: { sequenceId: string; contactId: string }) =>
      postJson<Enrollment>(`/api/sequences/${sequenceId}/enroll`, { contactId }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
      queryClient.invalidateQueries({ queryKey: ['sequence', variables.sequenceId] });
      queryClient.invalidateQueries({ queryKey: ['enrollments', variables.sequenceId] });
    },
  });
}

export function useUnenrollContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sequenceId, enrollmentId }: { sequenceId: string; enrollmentId: string }) =>
      postJson<void>(`/api/sequences/${sequenceId}/unenroll`, { enrollmentId }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sequences'] });
      queryClient.invalidateQueries({ queryKey: ['sequence', variables.sequenceId] });
      queryClient.invalidateQueries({ queryKey: ['enrollments', variables.sequenceId] });
    },
  });
}

export function useEnrollments(sequenceId: string | null) {
  return useQuery({
    queryKey: ['enrollments', sequenceId],
    queryFn: () => fetchJson<EnrollmentsResponse>(`/api/sequences/${sequenceId}/enrollments`),
    enabled: !!sequenceId,
  });
}
